import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SzamlazoService } from '../../szamlazo/szamlazo.service';
import { InventarioService } from '../../inventario/inventario.service';
import { GepjarmuService } from '../../gepjarmu/gepjarmu.service';
import { EszkozService } from '../../eszkoz/eszkoz.service';
import { UgyfelkorBeallitasComponent } from '../ugyfelkor-beallitas/ugyfelkor-beallitas.component';

/**
 *  ÜGYFÉLVÁLASZTÓ egy alrendszerben (Számla és Kassza, Készletkezelés, Tárgyi eszköz).
 *
 *  Minden alrendszernek önmagában is működnie kell (user, 2026-10-10), ezért mindegyiknek saját
 *  ügyfélválasztója van – mint a Könyvelésnek. A lista a modul ügyfélkör-módja szerint jön
 *  (backend/ugyfeltorzs.js): kapcsolt módban a Könyvelés ügyfelei, önálló módban a saját ügyfélkör,
 *  amit itt lehet bővíteni és szerkeszteni. A csempe / lista nézetet a böngésző megjegyzi.
 *
 *  Az útvonal `data.alr` mondja meg, melyik modulé az oldal.
 */
const MODULOK: Record<string, { nev: string; lista: string; kezdo: string; szerepes: boolean }> = {
  SZAMLAZO: { nev: 'Számla és Kassza', lista: '/api/szamlazo/ugyfelek/', kezdo: '/szamlazo/kezdolap', szerepes: true },
  INVENTARIO: { nev: 'Készletkezelés', lista: '/api/inventario/ugyfelek/', kezdo: '/inventario/kezdolap', szerepes: true },
  ESZKOZ: { nev: 'Tárgyi eszköz', lista: '/api/eszkoz/ugyfelek/', kezdo: '/eszkoz/eszkozok', szerepes: false },
  GEPJARMU: { nev: 'Gépjármű', lista: '/api/gepjarmu/ugyfelek/', kezdo: '/gepjarmu/kezdolap', szerepes: true },
};

@Component({
  selector: 'app-modul-ugyfelek',
  standalone: true,
  imports: [CommonModule, FormsModule, UgyfelkorBeallitasComponent],
  templateUrl: './modul-ugyfelek.component.html',
  styleUrls: ['./modul-ugyfelek.component.scss'],
})
export class ModulUgyfelekComponent implements OnInit {
  alr = '';
  ugyfelek: any[] = [];
  mod: any = null;
  kereso = '';
  csakAktiv = true;
  nezet: 'csempe' | 'lista' = 'csempe';
  betolt = true;
  hiba = '';
  szerk: any = null;
  szerkHiba = '';
  beallitasNyitva = false;
  aktivId: string | null = null;

  constructor(private http: HttpClient, private route: ActivatedRoute, private router: Router,
              private szamlazo: SzamlazoService, private inventario: InventarioService, private eszkoz: EszkozService,
              private gepjarmu: GepjarmuService) {}

  get modul() { return MODULOK[this.alr]; }
  intezmeny = '';
  get onallo() { return this.mod?.mod === 'onallo'; }
  private get nezetKulcs() { return `ugyfelNezet_${this.alr}`; }

  async ngOnInit() {
    this.alr = String(this.route.snapshot.data['alr'] || '').toUpperCase();
    try { this.nezet = localStorage.getItem(this.nezetKulcs) === 'lista' ? 'lista' : 'csempe'; } catch { /* privát ablak */ }
    //  Az intézmény a modul szolgáltatásából (a tárolt sajatIntezmeny): közvetlen URL-betöltéskor az
    //  app-keret még nem regisztrált, és tőle üres intézményt kapnánk – üres listával.
    const a = await this.szolgaltatas()?.init();
    this.intezmeny = a?.intezmeny || '';
    this.aktivId = a?.ugyfelId || null;
    await this.frissit();
  }
  nezetValt(n: 'csempe' | 'lista') { this.nezet = n; try { localStorage.setItem(this.nezetKulcs, n); } catch { /* privát ablak */ } }

  private szolgaltatas(): any {
    return this.alr === 'SZAMLAZO' ? this.szamlazo : this.alr === 'INVENTARIO' ? this.inventario : this.alr === 'ESZKOZ' ? this.eszkoz : this.alr === 'GEPJARMU' ? this.gepjarmu : null;
  }

  async frissit() {
    if (!this.modul || !this.intezmeny) { this.betolt = false; return; }
    this.betolt = true; this.hiba = '';
    try {
      const [lista, mod] = await Promise.all([
        firstValueFrom(this.http.get<any[]>(this.modul.lista + this.intezmeny)),
        firstValueFrom(this.http.get(`/api/ugyfeltorzs/${this.alr}/mod/${this.intezmeny}`)),
      ]);
      this.ugyfelek = lista || []; this.mod = mod;
    } catch (e: any) { this.hiba = e?.error?.error || 'Az ügyfelek nem tölthetők be.'; }
    this.betolt = false;
  }

  get szurt(): any[] {
    const k = this.kereso.trim().toLowerCase();
    return this.ugyfelek.filter((u) => (!this.csakAktiv || !Number(u.inaktiv))
      && (!k || `${u.nev || ''} ${u.adoszam || ''} ${u.azonosito || ''} ${u.telepules || ''}`.toLowerCase().includes(k)));
  }
  cim(u: any) { return [u.irsz, u.telepules].filter(Boolean).join(' ') + (u.cim ? `, ${u.cim}` : ''); }

  async valaszt(u: any) {
    const sz = this.szolgaltatas();
    if (!sz) return;
    if (this.alr === 'ESZKOZ') await sz.setAktiv({ ugyfelId: u.id, ugyfelNev: u.nev, konyveles_mod: u.konyveles_mod || 'egyszeres' });
    else await sz.setAktiv({ ugyfelId: u.id, ugyfelNev: u.nev, szerep: u.szerep || null });
    this.router.navigateByUrl(this.modul.kezdo);
  }

  /* ── saját ügyfél (önálló mód) ── */
  uj() { this.szerkHiba = ''; this.szerk = { nev: '', adoszam: '', irsz: '', telepules: '', cim: '', email: '', telefon: '', bankszamla: '', afa_alany: true, inaktiv: false }; }
  szerkeszt(u: any, ev: Event) { ev.stopPropagation(); this.szerkHiba = ''; this.szerk = { ...u, afa_alany: Number(u.afa_alany) !== 0, inaktiv: !!Number(u.inaktiv) }; }
  async ment() {
    this.szerkHiba = '';
    if (!String(this.szerk?.nev || '').trim()) { this.szerkHiba = 'A név kötelező.'; return; }
    try {
      if (this.szerk.id) await firstValueFrom(this.http.put(`/api/ugyfeltorzs/${this.alr}/ugyfel-modosit/${this.szerk.id}`, this.szerk));
      else await firstValueFrom(this.http.post(`/api/ugyfeltorzs/${this.alr}/ugyfel/${this.intezmeny}`, this.szerk));
      this.szerk = null;
      await this.frissit();
    } catch (e: any) { this.szerkHiba = e?.error?.error || 'A mentés nem sikerült.'; }
  }
  async modValtozott() { await this.frissit(); }
}
